import React, { useEffect, useImperativeHandle, useRef, useState, forwardRef } from 'react';
import api, { apiDownloadUrl } from '../api';

/**
 * Form upload Excel → POST /api/import-tasks (fields: file, project_id, team_id).
 */
function ExcelImportPanelInner(
  {
    defaultProjectId = '1',
    defaultTeamId = '',
    teams = [],
    onImported,
    title = 'Import công việc từ Excel',
    /** Có giá trị: hiển thị tên đội cố định, không dropdown; import dùng defaultTeamId */
    readOnlyTeamName = '',
    /** Giao diện gọn: không khung lớn; chỉ input file ẩn + trạng thái — dùng ref.openPicker() */
    compact = false,
    /** Với compact: chọn file xong tự gửi import */
    autoSubmitOnFileSelect = true,
  },
  ref
) {
  const [projectId, setProjectId] = useState(String(defaultProjectId));
  const [teamId, setTeamId] = useState(defaultTeamId != null ? String(defaultTeamId) : '');
  const [status, setStatus] = useState('');
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  useImperativeHandle(ref, () => ({
    openPicker: () => fileRef.current?.click(),
  }));

  useEffect(() => {
    setProjectId(String(defaultProjectId));
  }, [defaultProjectId]);

  useEffect(() => {
    setTeamId(defaultTeamId != null ? String(defaultTeamId) : '');
  }, [defaultTeamId]);

  async function runImport(file) {
    if (!file) {
      setStatus('Vui lòng chọn file .xlsx hoặc .xls.');
      return;
    }
    if (!teamId || String(teamId).trim() === '') {
      setStatus('Chọn đội thi công — mỗi đội chỉ import phần công việc của đội đó.');
      return;
    }
    setBusy(true);
    setStatus('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('project_id', projectId);
      fd.append('team_id', teamId);
      const res = await api.post('/import-tasks', fd);
      const { inserted = 0, errors = [] } = res.data || {};
      const firstErr = errors[0]?.error;
      const errPart = errors.length
        ? ` ${errors.length} dòng lỗi.${firstErr ? ` Ví dụ: ${firstErr}` : ''}`
        : '';
      setStatus(`Đã nhập ${inserted} công việc.${errPart}`);
      if (fileRef.current) fileRef.current.value = '';
      onImported?.();
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Import thất bại';
      setStatus(msg);
    } finally {
      setBusy(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    await runImport(file);
  }

  function onFileInputChange(ev) {
    const file = ev.target.files?.[0];
    if (compact && autoSubmitOnFileSelect && file) {
      runImport(file);
    }
  }

  if (compact) {
    return (
      <div style={{ display: 'block' }}>
        <input
          ref={fileRef}
          type="file"
          accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
          style={{
            position: 'absolute',
            width: 1,
            height: 1,
            padding: 0,
            margin: -1,
            overflow: 'hidden',
            clip: 'rect(0,0,0,0)',
            whiteSpace: 'nowrap',
            border: 0,
          }}
          onChange={onFileInputChange}
          tabIndex={-1}
        />
        {status ? (
          <p
            style={{
              marginTop: 10,
              marginBottom: 0,
              fontSize: 14,
              color:
                status.includes('thất bại') ||
                status.includes('lỗi') ||
                status.includes('Missing') ||
                status.includes('Thiếu') ||
                status.includes('Vui lòng')
                  ? '#b91c1c'
                  : '#15803d',
            }}
          >
            {status}
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div
      style={{
        marginTop: 16,
        marginBottom: 20,
        padding: 20,
        maxWidth: 520,
        borderRadius: 12,
        border: '1px solid #e2e8f0',
        background: 'linear-gradient(180deg, #f8fafc 0%, #fff 48%)',
        boxShadow: '0 1px 3px rgba(15,23,42,0.06)',
      }}
    >
      <h3 style={{ marginTop: 0, marginBottom: 14, fontSize: 17, color: '#0f172a' }}>{title}</h3>
      <p style={{ margin: '0 0 14px', fontSize: 13, color: '#64748b', lineHeight: 1.5 }}>
        Sheet đầu tiên: đủ 7 cột như API import (mẫu Phần móng hoặc lịch sàn/phần 2/4). Import gắn với{' '}
        <strong>đội</strong> đã chọn.{' '}
        <a href={apiDownloadUrl('sample-tasks-template')} download style={{ color: '#2563eb' }}>
          Tải file mẫu
        </a>
      </p>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14 }}>
          <span style={{ minWidth: 88, color: '#334155' }}>Dự án (ID)</span>
          <input
            type="text"
            inputMode="numeric"
            value={projectId}
            onChange={(ev) => setProjectId(ev.target.value)}
            style={{ width: 88, padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e1' }}
          />
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, flexWrap: 'wrap' }}>
          <span style={{ minWidth: 88, color: '#334155' }}>Đội thi công</span>
          {readOnlyTeamName ? (
            <span
              style={{
                minWidth: 220,
                padding: '8px 12px',
                borderRadius: 8,
                border: '1px solid #cbd5e1',
                background: '#f8fafc',
                color: '#0f172a',
                fontWeight: 600,
              }}
            >
              {readOnlyTeamName}
            </span>
          ) : teams.length > 0 ? (
            <select
              value={teamId}
              onChange={(ev) => setTeamId(ev.target.value)}
              style={{ minWidth: 220, padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e1' }}
            >
              <option value="">— Chọn đội —</option>
              {teams.map((t) => (
                <option key={t.id} value={String(t.id)}>
                  {t.name} (id {t.id})
                </option>
              ))}
            </select>
          ) : (
            <input
              type="text"
              inputMode="numeric"
              placeholder="ID đội (tạo trong trang dự án)"
              value={teamId}
              onChange={(ev) => setTeamId(ev.target.value)}
              style={{ width: 200, padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e1' }}
            />
          )}
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 14, color: '#334155' }}>
          <span>File Excel</span>
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
            style={{ fontSize: 13 }}
            onChange={onFileInputChange}
          />
        </label>
        <button
          type="submit"
          disabled={busy}
          style={{
            alignSelf: 'flex-start',
            padding: '10px 22px',
            border: 'none',
            borderRadius: 8,
            fontWeight: 600,
            fontSize: 14,
            cursor: busy ? 'wait' : 'pointer',
            background: busy ? '#94a3b8' : 'linear-gradient(180deg, #059669 0%, #047857 100%)',
            color: '#fff',
            boxShadow: busy ? 'none' : '0 1px 3px rgba(5,150,105,0.35)',
          }}
        >
          {busy ? 'Đang nhập…' : 'Tải lên và import'}
        </button>
      </form>
      {status && (
        <p
          style={{
            marginTop: 14,
            marginBottom: 0,
            fontSize: 14,
            color:
              status.includes('thất bại') ||
              status.includes('lỗi') ||
              status.includes('Missing') ||
              status.includes('Thiếu')
                ? '#b91c1c'
                : '#15803d',
          }}
        >
          {status}
        </p>
      )}
    </div>
  );
}

const ExcelImportPanel = forwardRef(ExcelImportPanelInner);
export default ExcelImportPanel;
